import React from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon as Icon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons";
import cl from "classnames";

import { InputComponent, InputHintProps, InputProps } from "./types";
import './Input.scss';
import '../base.scss'

export const getInputClasses = ({
    value,
    isInvalid = false,
    isValid = false,
    isEmpty,
}: {
    value?: any;
    isInvalid?: boolean;
    isValid?: boolean;
    isEmpty?: boolean;
}) => {
    return cl({
        'ew-input--error': isInvalid,
        'ew-input--valid': isValid,
        'ew-input--empty': typeof isEmpty !== 'undefined' ? isEmpty : !value,
    })
}

/**
 * Enhanced Input component with validation states and styling
 * 
 * Features:
 * - Automatic validation state styling (error/valid/empty)
 * - Consistent design system styling
 * - Support for all standard HTML input attributes
 * - Forwarded ref support
 * 
 * @example
 * // Basic usage
 * <Input 
 *   placeholder="Enter your name" 
 *   value={name} 
 *   onChange={(e) => setName(e.target.value)} 
 * />
 * 
 * @example
 * // With validation states
 * <Input 
 *   placeholder="Email address"
 *   type="email"
 *   isInvalid={!isValidEmail}
 *   isValid={isValidEmail && email.length > 0}
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 * />
 * 
 * @example
 * // With hint tooltip
 * <InputGroup>
 *   <Input 
 *     placeholder="Password"
 *     type="password"
 *     value={password}
 *     onChange={(e) => setPassword(e.target.value)}
 *   />
 *   <Input.Hint placement="top">
 *     Password must be at least 8 characters long
 *   </Input.Hint>
 * </InputGroup>
 *
 */
const Input = React.forwardRef(
    (
        {
            className,
            isInvalid = false,
            isValid = false,
            isEmpty,
            ...props
        }: InputProps,
        ref: React.Ref<HTMLInputElement>
    ) => {
        const inputStateClasses = getInputClasses({
            value: props.value,
            isInvalid,
            isValid,
            isEmpty,
        });
        return (<input
            ref={ref}
            className={cl('ew-input', className, inputStateClasses)}
            {...props}
        />);
    }
) as InputComponent;

/**
 * Hint component that displays an info icon with tooltip
 * 
 * Usage:
 * - Place inside an InputGroup for proper positioning
 * - Use to provide additional context or validation rules
 * 
 * @example
 * <InputGroup>
 *   <Input placeholder="Username" />
 *   <Input.Hint placement="top">
 *     Username must be unique and contain only letters and numbers
 *   </Input.Hint>
 * </InputGroup>
 */
export const InputHint = ({
    placement,
    children
}: InputHintProps) => {
    const tooltipComponent = (
        <Tooltip id="tooltip">
            {children}
        </Tooltip>
    );
    return (<div className="ew-input-hint">
        <OverlayTrigger placement={placement} overlay={tooltipComponent}>
            <Icon icon={faInfoCircle} />
        </OverlayTrigger>
    </div>)
};

Input.Hint = InputHint;

export default Input;